import { useEffect, useMemo, useRef } from 'react';
import * as echarts from 'echarts';
import type { EChartsType } from 'echarts';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '../lib/api';

export function ContactDensityChart({ theme }: { theme: 'light' | 'dark' }): JSX.Element {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<EChartsType | null>(null);
  const { data: densityResponse, isLoading } = useQuery({
    queryKey: ['contact-density'],
    queryFn: () =>
      apiFetch<{ squads: string[]; data: [number, number, number | null][] }>('/contact-density/'),
  });
  const squads = densityResponse?.squads ?? [];
  const data = densityResponse?.data ?? [];
  const maxValue = useMemo(
    () => data.reduce((max, point) => (typeof point[2] === 'number' ? Math.max(max, point[2]) : max), 0),
    [data]
  );

  useEffect(() => {
    if (!chartRef.current) return;
    chartInstance.current?.dispose();
    const instance = echarts.init(chartRef.current);
    chartInstance.current = instance;

    const resizeHandler = () => instance.resize();
    window.addEventListener('resize', resizeHandler);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      instance.dispose();
    };
  }, [theme]);

  useEffect(() => {
    if (!chartInstance.current) return;

    const styles = getComputedStyle(document.documentElement);
    const token = (name: string) => styles.getPropertyValue(name).trim();
    const normalize = (raw: string) => raw.replace(/\s+/g, ', ');
    const toHsl = (name: string, fallback: string) => {
      const raw = token(name);
      return raw ? `hsl(${normalize(raw)})` : fallback;
    };
    const toHsla = (name: string, alpha: number, fallback: string) => {
      const raw = token(name);
      return raw ? `hsla(${normalize(raw)}, ${alpha})` : fallback;
    };

    const axisColor = toHsl('--border', '#2a2d31');
    const textColor = toHsl('--muted-foreground', '#9ca3af');
    const cardColor = toHsl('--card', '#1a1d21');
    const foreground = toHsl('--foreground', '#e5e7eb');

    const option: echarts.EChartsOption = {
      tooltip: {
        position: 'top',
        backgroundColor: cardColor,
        borderColor: axisColor,
        textStyle: { color: foreground },
        formatter: (params: unknown) => {
          const dataValue = Array.isArray(params) ? params[0]?.value : (params as { value?: unknown })?.value;
          if (!Array.isArray(dataValue)) return '';
          const [xIdx, yIdx, count] = dataValue as [number, number, number | null];
          return `${squads[yIdx] ?? ''} → ${squads[xIdx] ?? ''}: <b>${count ?? 0}</b>`;
        },
      },
      grid: { left: 16, right: 20, top: 64, bottom: 40, containLabel: true },
      xAxis: {
        type: 'category',
        data: squads,
        name: 'Кого убили',
        nameLocation: 'middle',
        nameGap: 63,
        axisLine: { lineStyle: { color: axisColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, rotate: 45, fontSize: 14, lineHeight: 20, interval: 0, margin: 16 },
        nameTextStyle: { color: textColor, fontSize: 14, fontWeight: 600 },
        splitArea: { show: false },
      },
      yAxis: {
        type: 'category',
        data: squads,
        name: 'Кто убил',
        nameGap: 24,
        axisLine: { lineStyle: { color: axisColor } },
        axisTick: { show: false },
        axisLabel: { color: textColor, fontSize: 14, lineHeight: 20, interval: 0, margin: 16 },
        nameTextStyle: { color: textColor, fontSize: 14, fontWeight: 600 },
        splitArea: { show: false },
      },
      visualMap: {
        min: 0,
        max: Math.max(maxValue, 1),
        show: false,
        calculable: false,
        inRange: {
          color: [
            toHsla('--primary', 0.08, 'rgba(255,222,0,0.1)'),
            toHsla('--primary', 0.4, 'rgba(255,222,0,0.45)'),
            toHsla('--primary', 0.85, 'rgba(255,222,0,0.9)'),
          ],
        },
      },
      series: [
        {
          name: 'Плотность контактов',
          type: 'heatmap',
          data,
          label: {
            show: true,
            formatter: (params: { value?: unknown }) =>
              Array.isArray(params.value) ? String(params.value[2] ?? '') : '',
            color: '#000',
            fontWeight: 500,
            fontSize: 12,
          },
          itemStyle: {
            borderColor: cardColor,
            borderWidth: 2.5,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: toHsla('--ring', 0.3, 'rgba(47,128,237,0.25)'),
            },
          },
          animation: false,
        },
      ],
    };

    chartInstance.current.setOption(option, true);
  }, [theme, data, squads, maxValue]);

  return (
    <div className="card p-4 space-y-4">
      <div>
        <p className="text-2xl font-semibold">Плотность контактов между отрядами</p>
        <p className="text-sm text-muted-foreground">Результативность встречных боёв</p>
      </div>
      <motion.div
        className="relative h-[880px] min-h-[880px]"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div ref={chartRef} className="h-full w-full" />
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-end gap-2">
              {[0, 1, 2, 3].map((index) => (
                <motion.div
                  key={index}
                  className="w-2 rounded-sm bg-primary/70"
                  animate={{ height: [12, 36, 16] }}
                  transition={{ duration: 1.2, repeat: Infinity, delay: index * 0.15 }}
                />
              ))}
            </div>
          </div>
        ) : null}
      </motion.div>
    </div>
  );
}
